import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FALLBACK = "/brand/logo-rumore-zero.png";
const POLL_MS = 60 * 1000;

const preload = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = reject;
    img.src = url;
  });

const ProgramInfo = ({ program }) => (
  <div data-testid="onair-program" className="mt-3 flex items-start gap-3 px-1">
    <span className="mt-1.5 relative inline-flex w-2 h-2 shrink-0">
      <span className="absolute inline-flex w-full h-full rounded-full bg-[#FF6B00] opacity-70 animate-ping" />
      <span className="relative inline-flex w-2 h-2 rounded-full bg-[#FF6B00]" />
    </span>
    <div className="min-w-0">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF6B00]">
        In onda · {program.start}–{program.end}
      </p>
      <h3 data-testid="onair-program-name" className="font-anton uppercase text-xl sm:text-2xl leading-tight text-[#F5F2EB]">
        {program.name}
      </h3>
      {program.subtitle && (
        <p data-testid="onair-program-subtitle" className="font-mono text-xs sm:text-sm text-[#A39A8F] leading-snug">
          {program.subtitle}
        </p>
      )}
    </div>
  </div>
);

export default function OnAirBanner() {
  const [src, setSrc] = useState(null);
  const [program, setProgram] = useState(null);

  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/onair`, { params: { _: Date.now() }, timeout: 10000 });
        if (stop) return;
        setProgram(data.program);
        const next = data.image ? `${BACKEND}${data.image}` : FALLBACK;
        await preload(next).catch(() => null);
        if (!stop) setSrc((cur) => (cur === next ? cur : next));
      } catch {
        if (!stop) setSrc((cur) => cur || FALLBACK);
      }
    };
    check();
    const id = setInterval(check, POLL_MS);
    const now = new Date();
    const toQuarter = (15 - (now.getMinutes() % 15)) * 60000 - now.getSeconds() * 1000 + 2000;
    const hourT = setTimeout(check, toQuarter);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop = true;
      clearInterval(id);
      clearTimeout(hourT);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const isFallback = !src || src === FALLBACK;

  return (
    <section data-testid="onair-banner-section" className="bg-[#12100e]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-2 pb-5 sm:pb-8">
        <div className={`rounded-xl sm:rounded-2xl overflow-hidden border border-[#332f2b] bg-[#0d0b0a] flex items-center justify-center ${isFallback ? "aspect-[2216/709]" : ""}`}>
          <img
            key={src || FALLBACK}
            src={src || FALLBACK}
            alt={isFallback ? "Radio Rumore Zero" : program ? `In onda: ${program.name}` : "In onda ora"}
            data-testid={isFallback ? "onair-banner-fallback" : "onair-banner-image"}
            className={`animate-in fade-in duration-700 ${isFallback ? "h-3/5 w-auto max-w-[80%] object-contain opacity-90" : "block w-full h-auto"}`}
            decoding="async"
            draggable={false}
          />
        </div>
        {program && <ProgramInfo program={program} />}
      </div>
    </section>
  );
}
